import { Box, Paper, Text, ThemeIcon } from "@mantine/core";
import {
  IconArrowsDownUp,
  IconBriefcase2,
  IconBriefcaseOff,
  IconBookmark,
  IconSend,
  type Icon,
} from "@tabler/icons-react";

type DashboardCardTitle =
  | "Requests Sent"
  | "Requests Received"
  | "Active Collaborations"
  | "Closed Collaborations"
  | "Shortlist";

type DashboardCardProps = {
  title: DashboardCardTitle | string;
  /** Shorter label when `compact` (e.g. mobile row layout). */
  compactTitle?: string;
  count: number;
  description: string;
  onClick?: () => void;
  compact?: boolean;
};

type CardVisualConfig = {
  icon: Icon;
  iconColor: string;
  iconBg: string;
};

const DEFAULT_CONFIG: CardVisualConfig = {
  icon: IconArrowsDownUp,
  iconColor: "#2563EB",
  iconBg: "#EFF6FF",
};

const TITLE_VISUALS: Record<string, CardVisualConfig> = {
  "Requests Sent": {
    icon: IconSend,
    iconColor: "#2563EB",
    iconBg: "#EFF6FF",
  },
  "Requests Received": {
    icon: IconArrowsDownUp,
    iconColor: "#3B82F6",
    iconBg: "#EEF2FF",
  },
  "Active Collaborations": {
    icon: IconBriefcase2,
    iconColor: "#F59E0B",
    iconBg: "#FEF3C7",
  },
  "Closed Collaborations": {
    icon: IconBriefcaseOff,
    iconColor: "#F97316",
    iconBg: "#FFEDD5",
  },
  Shortlist: {
    icon: IconBookmark,
    iconColor: "#7C3AED",
    iconBg: "#F3E8FF",
  },
};

const DASHBOARD_CARD_BACKGROUND = "#ebf0ff";
const DASHBOARD_TEXT_DARK_BLUE = "#04305c";
const DASHBOARD_BORDER_DARK_BLUE = "#04305c";

export default function DashboardCard({
  title,
  compactTitle,
  count,
  description,
  onClick,
  compact = false,
}: DashboardCardProps) {
  const visual = TITLE_VISUALS[title] ?? DEFAULT_CONFIG;
  const IconComponent = visual.icon;
  const displayTitle = compact && compactTitle ? compactTitle : title;

  return (
    <Paper
      radius="md"
      p={compact ? "xs" : "md"}
      h="100%"
      onClick={onClick}
      style={{
        border: `1px solid ${DASHBOARD_BORDER_DARK_BLUE}`,
        backgroundColor: DASHBOARD_CARD_BACKGROUND,
        cursor: onClick ? "pointer" : "default",
      }}
    >
      <Box
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: compact ? 6 : 12,
        }}
      >
        <Box style={{ minWidth: 0, flex: 1 }}>
          <Text
            fw={600}
            size={compact ? "xs" : "sm"}
            c={DASHBOARD_TEXT_DARK_BLUE}
            lh={1.2}
            lineClamp={compact ? 2 : undefined}
          >
            {displayTitle}
          </Text>
          <Text fw={700} fz={compact ? 20 : 30} c={DASHBOARD_TEXT_DARK_BLUE} lh={1.1} mt={compact ? 4 : 6}>
            {count}
          </Text>
          {!compact && (
            <Text size="xs" c={DASHBOARD_TEXT_DARK_BLUE} mt={10} style={{ lineHeight: 1.35 }}>
              {description}
            </Text>
          )}
        </Box>
        <ThemeIcon
          radius="md"
          size={compact ? 26 : 32}
          variant="light"
          style={{
            backgroundColor: visual.iconBg,
            color: visual.iconColor,
            flexShrink: 0,
          }}
        >
          <IconComponent size={compact ? 14 : 18} />
        </ThemeIcon>
      </Box>
    </Paper>
  );
}
